import { el, clear, toast } from '../core/dom.js';
import { activeHabits, getState, planFor } from '../core/state.js';
import { events as eventsStore, reflections } from '../storage/db.js';
import { uuid } from '../core/ids.js';
import { localDate, nowIso } from '../core/time.js';
import { currentRoute, navigate } from '../router.js';
import { suggestPlanAdjustment } from '../plans/adaptation.js';
import { containsCrisisLanguage } from '../copy/crisis.js';
import { copy } from '../copy/tone.js';
import type { HabitRecord } from '../core/types.js';

function showCrisisCard(parent: HTMLElement): void {
  const tone = getState().settings.tone;
  parent.appendChild(el('div', { class: 'card', attrs: { style: 'border-color:#f87171;' } },
    el('h2', { attrs: { style: 'margin:0;' } }, 'Support is available'),
    el('p', { class: 'muted' }, copy('safety_crisis', tone)),
    el('ul', { attrs: { style: 'margin:0.5rem 0 0 1rem; padding:0; font-size:0.9rem;' } },
      el('li', {}, 'US: 988 (Suicide & Crisis Lifeline)'),
      el('li', {}, 'UK & Ireland: 116 123 (Samaritans)'),
      el('li', {}, 'International: findahelpline.com'),
    ),
    el('a', { href: '#/resources', class: 'btn block', attrs: { style: 'margin-top:0.5rem;' } },
      'Open support resources'),
  ));
}

type PromptId = 'better' | 'harder' | 'experiment';
const PROMPTS: Array<{ id: PromptId; label: string; placeholder: string }> = [
  { id: 'better', label: 'What went better than expected?', placeholder: 'A small win, a wait, a different choice…' },
  { id: 'harder', label: 'What made things harder?', placeholder: 'A trigger, a tough day, an unexpected thing…' },
  { id: 'experiment', label: 'One tiny adjustment for next week?', placeholder: 'The thing you want to try…' },
];

const CHIPS_BY_PROMPT: Record<PromptId, string[]> = {
  better: ['waited', 'rescue helped', 'support', 'self-aware', 'planned', 'celebrated'],
  harder: ['stress', 'boredom', 'social', 'tired', 'after_work', 'after_meals', 'before_bed', 'travel'],
  experiment: ['shorter target', 'longer target', 'new rescue', 'add buffer', 'avoid trigger', 'rest day'],
};

const EXPERIMENT_OPTIONS = [
  { value: 'lower_target', label: 'Make this week\'s target a touch easier' },
  { value: 'raise_target', label: 'Stretch the target a little' },
  { value: 'pause_briefly', label: 'Pause the plan for a day or two' },
  { value: 'try_window', label: 'Try a time-window approach' },
  { value: 'no_change', label: 'No change — just observe' },
];

export async function renderReflection(parent: HTMLElement): Promise<void> {
  clear(parent);
  const st = getState();
  const habitsList = activeHabits(st);
  if (habitsList.length === 0) { parent.appendChild(el('p', {}, 'No active plan.')); return; }
  const requested = currentRoute().params.get('habit');
  const habit = habitsList.find((h) => h.id === requested) ?? st.activeHabit ?? habitsList[0];
  if (!habit) { parent.appendChild(el('p', {}, 'No active plan.')); return; }
  await renderForm(parent, habit);
}

async function renderForm(parent: HTMLElement, habit: HabitRecord): Promise<void> {
  clear(parent);
  const drafts: Record<PromptId, { text: string; chips: Set<string> }> = {
    better: { text: '', chips: new Set() },
    harder: { text: '', chips: new Set() },
    experiment: { text: '', chips: new Set() },
  };
  let experiment: string | undefined;

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Weekly reflection'),
    el('p', { class: 'muted' },
      `Three small notes about ${habit.privateLabel ?? habit.label}. Skip whatever you want — three minutes is plenty.`),
  ));

  for (const p of PROMPTS) {
    const draft = drafts[p.id];
    const ta = el('textarea', {
      rows: 2,
      placeholder: p.placeholder,
      oninput: (e: Event) => draft.text = (e.target as HTMLTextAreaElement).value,
    }) as HTMLTextAreaElement;
    const chipRow = el('div', { class: 'kbd-list' });
    for (const c of CHIPS_BY_PROMPT[p.id]) {
      const tag = el('button', {
        class: 'tag',
        attrs: { type: 'button', 'aria-pressed': 'false' },
      }, c.replace(/_/g, ' '));
      tag.onclick = () => {
        const set = draft.chips;
        if (set.has(c)) {
          set.delete(c);
          tag.classList.remove('active');
          tag.setAttribute('aria-pressed', 'false');
        } else {
          set.add(c);
          tag.classList.add('active');
          tag.setAttribute('aria-pressed', 'true');
        }
      };
      chipRow.appendChild(tag);
    }
    parent.appendChild(el('div', { class: 'card' },
      el('h2', { attrs: { style: 'font-size:1rem;' } }, p.label),
      ta,
      el('div', { class: 'muted', attrs: { style: 'margin: 0.5rem 0 0.25rem; font-size:0.85rem;' } }, 'Tags'),
      chipRow,
    ));
  }

  // Experiment picker — drives plan suggestion when applied.
  const experimentCard = el('div', { class: 'card' },
    el('h2', { attrs: { style: 'font-size:1rem;' } }, 'Next experiment'),
    el('p', { class: 'muted', attrs: { style: 'margin:0 0 0.5rem; font-size:0.85rem;' } },
      'Pick one — it stays linked to this reflection.'),
  );
  const list = el('div', { class: 'choice-list' });
  for (const opt of EXPERIMENT_OPTIONS) {
    const c = el('button', {
      class: 'choice', attrs: { type: 'button' },
      onclick: () => {
        experiment = opt.value;
        for (const child of [...list.children]) {
          const isMatch = (child as HTMLElement).dataset['value'] === opt.value;
          (child as HTMLElement).classList.toggle('selected', isMatch);
        }
      },
    }, opt.label);
    c.dataset['value'] = opt.value;
    list.appendChild(c);
  }
  experimentCard.appendChild(list);
  parent.appendChild(experimentCard);

  parent.appendChild(el('div', { class: 'stack' },
    el('button', {
      class: 'btn primary block',
      onclick: async (e: Event) => {
        const btn = e.currentTarget as HTMLButtonElement;
        if (btn.disabled) return;
        btn.disabled = true;
        try {
          let saved = 0;
          for (const p of PROMPTS) {
            const d = drafts[p.id];
            if (!d.text.trim() && d.chips.size === 0) continue;
            await reflections.put({
              id: uuid(), habitId: habit.id, promptId: p.id,
              text: d.text.trim() || undefined,
              chips: [...d.chips],
              createdAt: nowIso(), localDate: localDate(),
            });
            saved++;
          }
          if (experiment) {
            await reflections.put({
              id: uuid(), habitId: habit.id, promptId: 'experiment_choice',
              text: experiment,
              chips: [],
              createdAt: nowIso(), localDate: localDate(),
            });
            saved++;
          }
          if (saved === 0) { toast('Nothing to save.'); btn.disabled = false; return; }
          const allText = PROMPTS.map((p) => drafts[p.id]?.text ?? '').join(' ');
          const crisis = containsCrisisLanguage(allText);
          await renderConfirmation(parent, habit, experiment);
          if (crisis) showCrisisCard(parent);
        } catch (err) {
          console.error('Save reflection failed:', err);
          toast('Could not save — try again.');
          btn.disabled = false;
        }
      },
    }, 'Save reflection'),
    el('button', { class: 'btn ghost block', onclick: () => navigate('/progress') }, 'Cancel'),
  ));
}

async function renderConfirmation(
  parent: HTMLElement,
  habit: HabitRecord,
  experiment: string | undefined,
): Promise<void> {
  clear(parent);
  const st = getState();
  const { plan, planState } = planFor(habit.id, st);
  parent.appendChild(el('div', { class: 'card center', attrs: { style: 'border-color:var(--accent-soft);' } },
    el('h2', {}, 'Reflection saved.'),
    el('p', { class: 'muted' },
      experiment ? `Next experiment: ${labelForExperiment(experiment)}.` : 'Nice work showing up to the page.'),
  ));

  if (plan && planState && (experiment === 'lower_target' || experiment === 'raise_target')) {
    const events = await eventsStore.byHabit(habit.id);
    const confidence = st.meta.onboarding_metadata?.confidence;
    const suggestion = suggestPlanAdjustment({ plan, planState, events, ...(confidence !== undefined ? { confidence } : {}) });
    if (suggestion && suggestion.apply && suggestion.kind !== 'hold_steady') {
      parent.appendChild(el('div', { class: 'card', attrs: { style: 'border-color:var(--accent-soft);' } },
        el('div', {
          class: 'muted',
          attrs: { style: 'text-transform:uppercase;letter-spacing:0.06em;font-size:0.72rem;margin-bottom:0.35rem;' },
        }, 'Plan suggestion'),
        el('h2', { attrs: { style: 'margin:0; font-size:1rem;' } }, suggestion.headline),
        el('p', { class: 'muted', attrs: { style: 'margin:0.35rem 0 0.5rem; font-size:0.9rem;' } }, suggestion.evidence),
        el('button', {
          class: 'btn primary', attrs: { type: 'button' },
          onclick: async () => {
            await suggestion.apply!();
            toast('Target updated.');
            window.dispatchEvent(new CustomEvent('wean:state-changed'));
            navigate('/progress', { habit: habit.id });
          },
        }, 'Apply now'),
      ));
    }
  }

  parent.appendChild(el('div', { class: 'stack' },
    el('button', {
      class: 'btn block',
      onclick: () => navigate('/progress', { habit: habit.id }),
    }, 'Back to Progress'),
    el('button', {
      class: 'btn ghost block',
      onclick: () => navigate('/today'),
    }, 'Back to Now'),
  ));
}

function labelForExperiment(value: string): string {
  const found = EXPERIMENT_OPTIONS.find((o) => o.value === value);
  return found ? found.label.toLowerCase() : value;
}
